// Single summary-tile primitive used by the deliverables SummaryStrip.
// Local to this folder (not extracted from KpiCard) — the two tabs'
// summary languages diverge enough that premature abstraction would
// add more rigidity than reuse. Matches the surface of KpiSummaryStrip
// tiles so the page reads with a single visual vocabulary in both
// light and dark modes.
//
// When `onClick` is supplied the tile becomes a filter-toggle button:
// keyboard-focusable, aria-pressed driven by `isActive`, brand ring
// when active. Without `onClick` the tile stays a plain <div> so
// non-filter usages (e.g. read-only dashboards) render unchanged.
//
// Optional `breakdown` slot renders below the value (above the
// subtitle) for cards that need to show component composition — used
// by the Total contract value card to host a stacked-bar breakdown.

import type { ReactNode } from "react";

export type MetricCardTone =
  | "danger"
  | "warning"
  | "neutral"
  | "success"
  | "brand"
  | "brandStrong";

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  valueTone?: MetricCardTone;
  subtitle?: ReactNode;
  /** Optional content rendered between the value and the subtitle.
   *  Used by composition-aware cards (e.g. Total contract value with a
   *  stacked-bar breakdown). Pass `undefined` to keep the existing
   *  value-then-subtitle layout. */
  breakdown?: ReactNode;
  /** Plain-language summary of the breakdown for assistive tech. When
   *  provided, applied as `aria-label` on the card. */
  ariaLabel?: string;
  testId: string;
  onClick?: () => void;
  isActive?: boolean;
}

const VALUE_TONE_CLASS: Record<MetricCardTone, string> = {
  danger: "text-red-600 dark:text-red-400",
  warning: "text-amber-600 dark:text-amber-400",
  neutral: "text-slate-900 dark:text-slate-100",
  success: "text-emerald-600 dark:text-emerald-400",
  brand: "text-brand-600 dark:text-brand-400",
  // Deeper brand shade for the meetings "Upcoming this week" tile —
  // matches the brand-700 used on the meeting due-soon pill so the two
  // surfaces share one visual cue.
  brandStrong: "text-brand-700 dark:text-brand-400",
};

const BASE_SURFACE_NO_BORDER = "rounded-md p-4";

// Default (inactive / non-interactive) palette — slate border + white
// surface so the inactive state recedes into the page background.
const INACTIVE_PALETTE =
  "border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/60";

const INTERACTIVE_CLASSES =
  "text-left w-full cursor-pointer transition-colors hover:border-slate-300 dark:hover:border-slate-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";

// Active palette — brand-tinted background + brand
// border + ring so a clicked tile reads as definitively selected
// against the inactive slate siblings. The 2px ring alone
// was too subtle next to the existing slate border.
const ACTIVE_PALETTE =
  "border border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-950/40 ring-2 ring-brand-500";

export default function MetricCard({
  label,
  value,
  valueTone = "neutral",
  subtitle,
  breakdown,
  ariaLabel,
  testId,
  onClick,
  isActive,
}: MetricCardProps) {
  const children = (
    <>
      <div className="text-caption font-medium text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div
        data-testid={`${testId}-value`}
        className={`mt-1 text-h2 font-semibold ${VALUE_TONE_CLASS[valueTone]}`}
      >
        {value}
      </div>
      {breakdown && (
        <div data-testid={`${testId}-breakdown`}>{breakdown}</div>
      )}
      {subtitle && (
        <div
          data-testid={`${testId}-subtitle`}
          className="mt-1 text-caption text-slate-500 dark:text-slate-400"
        >
          {subtitle}
        </div>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        aria-pressed={!!isActive}
        aria-label={ariaLabel}
        className={`${BASE_SURFACE_NO_BORDER} ${
          isActive ? ACTIVE_PALETTE : INACTIVE_PALETTE
        } ${INTERACTIVE_CLASSES}`}
      >
        {children}
      </button>
    );
  }

  return (
    <div
      data-testid={testId}
      aria-label={ariaLabel}
      className={`${BASE_SURFACE_NO_BORDER} ${INACTIVE_PALETTE}`}
    >
      {children}
    </div>
  );
}
